import { Injectable, signal } from '@angular/core'
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr'
import { Subject } from 'rxjs'

import {
    ContentChangeNotification,
    WhiteboardChangeNotification,
} from './meeting-entity-types'

export type {
    ContentChangeNotification,
    MeetingEntityType,
    WhiteboardChangeNotification,
} from './meeting-entity-types'
export { MeetingEntityTypes } from './meeting-entity-types'

export interface MeetingParticipant {
    userId: string
    displayName: string
}

export interface RosterUpdate {
    meetingId: string
    leadUserId: string
    leadConnectionId: string
    roster: MeetingParticipant[]
    /** True while the lead is disconnected but may reconnect within the grace window. */
    leadReconnecting?: boolean
}

export interface LeadUpdate {
    meetingId: string
    leadUserId: string
    leadConnectionId: string
}

export interface MeetingTimerSnapshot {
    overallSeconds: number
    pageSeconds: number[]
    currentPageIndex: number | null
}

// Re-export MeetingSnapshot shape (defined here for hub join responses)
export interface MeetingSnapshot {
    meetingId: string
    leadUserId: string
    isLead: boolean
    currentPageIndex: number
    roster: MeetingParticipant[]
    timer: MeetingTimerSnapshot | null
}

export interface LiveMeetingChangedNotification {
    meetingId: string
    isLive: boolean
}

/**
 * Wraps the SignalR connection to the meeting hub (/hubs/meeting).
 * Cookie auth flows automatically on the same-origin WebSocket handshake, so no token factory is needed.
 * A single connection is shared per browser; the active meeting component drives it.
 */
@Injectable({
    providedIn: 'root',
})
export class MeetingHubService {
    private connection: HubConnection | null = null
    private currentMeetingId: string | null = null
    private connectPromise: Promise<void> | null = null
    private watchingLiveMeetings = false

    /** Emits the new page index when the lead navigates (followers). */
    readonly pageChanged$ = new Subject<number>()
    readonly rosterChanged$ = new Subject<RosterUpdate>()
    readonly leadChanged$ = new Subject<LeadUpdate>()
    readonly meetingEnded$ = new Subject<string>()
    readonly timerUpdated$ = new Subject<MeetingTimerSnapshot>()
    readonly contentChanged$ = new Subject<ContentChangeNotification>()
    readonly whiteboardChanged$ = new Subject<WhiteboardChangeNotification>()
    /** Emits when a meeting session starts or ends (for the meetings list page). */
    readonly liveMeetingChanged$ = new Subject<LiveMeetingChangedNotification>()
    /** Emits a fresh snapshot after an automatic reconnect re-joins the meeting. */
    readonly reSynced$ = new Subject<MeetingSnapshot>()

    readonly connectionState = signal<HubConnectionState>(HubConnectionState.Disconnected)

    /** Id of the meeting this tab is actively joined to (null when not in a live session). */
    readonly activeMeetingId = signal<string | null>(null)

    /** Whether this tab's connection is the meeting lead. */
    readonly isLead = signal(false)

    /** This browser's current SignalR connection id (changes across reconnects). */
    get connectionId(): string | null {
        return this.connection?.connectionId ?? null
    }

    private buildConnection(): HubConnection {
        const connection = new HubConnectionBuilder()
            .withUrl('/hubs/meeting')
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Warning)
            .build()

        connection.on('PageChanged', (pageIndex: number) => this.pageChanged$.next(pageIndex))
        connection.on('RosterChanged', (update: RosterUpdate) => this.rosterChanged$.next(update))
        connection.on('LeadChanged', (update: LeadUpdate) => this.leadChanged$.next(update))
        connection.on('MeetingEnded', (meetingId: string) => this.meetingEnded$.next(meetingId))
        connection.on('TimerUpdated', (timer: MeetingTimerSnapshot) => this.timerUpdated$.next(timer))
        connection.on('ContentChanged', (notification: ContentChangeNotification) => {
            console.debug('[MeetingHub] ContentChanged', notification)
            this.contentChanged$.next(notification)
        })
        connection.on('WhiteboardChanged', (notification: WhiteboardChangeNotification) => {
            console.debug('[MeetingHub] WhiteboardChanged', notification.meetingId)
            this.whiteboardChanged$.next(notification)
        })
        connection.on('LiveMeetingChanged', (notification: LiveMeetingChangedNotification) =>
            this.liveMeetingChanged$.next(notification),
        )

        connection.onreconnecting(() => this.connectionState.set(HubConnectionState.Reconnecting))
        connection.onreconnected(async () => {
            this.connectionState.set(HubConnectionState.Connected)
            try {
                if (this.watchingLiveMeetings) {
                    await this.connection!.invoke('WatchLiveMeetings')
                }
                if (this.currentMeetingId) {
                    const snapshot = await this.connection!.invoke<MeetingSnapshot>('JoinMeeting', this.currentMeetingId)
                    this.reSynced$.next(snapshot)
                }
            } catch (err) {
                console.error('Failed to restore meeting hub state after reconnect', err)
            }
        })
        connection.onclose(() => this.connectionState.set(HubConnectionState.Disconnected))

        return connection
    }

    private async ensureConnected(): Promise<void> {
        if (!this.connection) {
            this.connection = this.buildConnection()
        }

        if (this.connection.state === HubConnectionState.Connected) return

        if (this.connection.state === HubConnectionState.Connecting || this.connection.state === HubConnectionState.Reconnecting) {
            await this.waitUntilConnected()
            return
        }

        if (!this.connectPromise) {
            this.connectPromise = this.connection
                .start()
                .then(() => this.connectionState.set(HubConnectionState.Connected))
                .finally(() => {
                    this.connectPromise = null
                })
        }
        await this.connectPromise
    }

    /** Waits until the hub is Connected, or rejects if it stays disconnected past the timeout. */
    private waitUntilConnected(timeoutMs = 15000): Promise<void> {
        if (this.connection?.state === HubConnectionState.Connected) {
            return Promise.resolve()
        }

        return new Promise((resolve, reject) => {
            const started = Date.now()
            const interval = setInterval(() => {
                const state = this.connection?.state
                if (state === HubConnectionState.Connected) {
                    clearInterval(interval)
                    resolve()
                    return
                }
                if (Date.now() - started >= timeoutMs || state === HubConnectionState.Disconnected) {
                    clearInterval(interval)
                    reject(new Error(`Meeting hub is not connected (state: ${state ?? 'none'}).`))
                }
            }, 100)
        })
    }

    /**
     * Ensures the hub is connected (waiting through a reconnect if needed) then invokes a hub method.
     * Throws if the invoke cannot be delivered — callers must not treat a silent skip as success.
     */
    private async invokeWhenReady(methodName: string, ...args: unknown[]): Promise<void> {
        await this.ensureConnected()
        if (this.connection!.state !== HubConnectionState.Connected) {
            await this.waitUntilConnected()
        }
        await this.connection!.invoke(methodName, ...args)
    }

    /** Connects (if needed) and joins the meeting, returning the current live snapshot. */
    async joinMeeting(meetingId: string): Promise<MeetingSnapshot> {
        await this.ensureConnected()
        this.currentMeetingId = meetingId
        this.activeMeetingId.set(meetingId)
        const snapshot = await this.connection!.invoke<MeetingSnapshot>('JoinMeeting', meetingId)
        this.isLead.set(snapshot.isLead)
        return snapshot
    }

    /** Lead-only: navigate the meeting to a new agenda page. */
    async setActivePage(meetingId: string, pageIndex: number): Promise<void> {
        await this.invokeWhenReady('SetActivePage', meetingId, pageIndex)
    }

    /** Lead-only: hand off leadership to another connected attendee. */
    async reassignLead(meetingId: string, newLeadUserId: string): Promise<void> {
        await this.invokeWhenReady('ReassignLead', meetingId, newLeadUserId)
    }

    /** Lead-only: end the meeting for everyone. Throws if the end cannot be delivered. */
    async endMeeting(meetingId: string): Promise<void> {
        await this.invokeWhenReady('EndMeeting', meetingId)
    }

    /** Lead-only: broadcast the authoritative timer snapshot. */
    async updateTimer(meetingId: string, timer: MeetingTimerSnapshot): Promise<void> {
        await this.invokeWhenReady('UpdateTimer', meetingId, timer)
    }

    /** Emits after reconnect so visible page components can catch up on missed changes. */
    readonly contentResync$ = new Subject<string>()

    requestContentResync(meetingId: string): void {
        this.contentResync$.next(meetingId)
    }

    /** Connect and subscribe to live-meeting start/end notifications (meetings list page). */
    async watchLiveMeetings(): Promise<void> {
        this.watchingLiveMeetings = true
        await this.ensureConnected()
        await this.connection!.invoke('WatchLiveMeetings')
    }

    /** Stop live-meeting notifications and tear down the hub if not in a meeting. */
    async unwatchLiveMeetings(): Promise<void> {
        this.watchingLiveMeetings = false
        await this.teardownConnectionIfIdle()
    }

    /** Broadcast whiteboard HTML to other attendees (lead or any attendee may type). */
    async sendWhiteboard(meetingId: string, html: string): Promise<void> {
        await this.invokeWhenReady('SendWhiteboard', meetingId, html)
    }

    /** Leaves the current meeting; keeps the hub open when watching the meetings list. */
    async leaveMeeting(): Promise<void> {
        this.currentMeetingId = null
        this.activeMeetingId.set(null)
        this.isLead.set(false)
        await this.teardownConnectionIfIdle()
    }

    private async teardownConnectionIfIdle(): Promise<void> {
        if (this.watchingLiveMeetings || this.currentMeetingId) return

        if (this.connection && this.connection.state !== HubConnectionState.Disconnected) {
            try {
                await this.connection.stop()
            } catch (err) {
                console.error('Error stopping meeting hub connection', err)
            }
        }
        this.connectionState.set(HubConnectionState.Disconnected)
    }
}
